import React from 'react';
import CurrencyText from '../../components/ui/labels/CurrencyText';
import { Banknote, Building2, HandCoins } from 'lucide-react';
import DonutChart2 from '../../components/ui/charts/DonutChart2';
import ProgressDonutChart from '../../components/ui/charts/ProgressDonutChart';
import BlurScopeContent from '../../components/ui/overlays/BlurScopeContent';
import RetirementProjectionChart from '../../components/ui/charts/RetirementProjectionChart';
import DreamInsight from '../../components/ui/labels/DreamInsight';
import BarRoundedChartStack from '../../components/ui/charts/BarRoundedChartStack';
import BarRoundedChart from '../../components/ui/charts/BarRoundedChart';

const Dashboard = ({ data }) => {
  const insightTypes = ['ok', 'problem', 'warning'];

  return (
    <div className="">
      <div className="flex flex-col gap-5">
        {/* Linha 1 */}
        <div className="grid grid-cols-1 lg:grid-cols-10 gap-5">
          {/* Patrimônio */}
          <div className="flex flex-col lg:col-span-3 gap-5">
            <div className="bg-white rounded-custom20 px-4 py-4 md:px-6 flex flex-col gap-2">
              <h2 className="text-lg text-blueDarker font-semibold">Patrimônio total</h2>
              <CurrencyText size="large" value={data.totalProperties} />
            </div>

            <div className="flex flex-col sm:flex-row gap-4 sm:gap-5 h-full">
              <div className="bg-white rounded-custom20 w-full px-4 py-4 md:px-6">
                <div className="flex gap-2 items-center mb-2">
                  <Banknote className="text-tealMedium" size={20} strokeWidth={1.5} />
                  <h2 className="text-lg text-blueDarker font-semibold">Financeiro</h2>
                </div>
                <CurrencyText size="small" value={data.totalFinancialProperties} color="text-tealMedium" />
              </div>

              <div className="bg-white rounded-custom20 w-full px-4 py-4 md:px-6">
                <div className="flex gap-2 items-center mb-2">
                  <Building2 className="text-tealMedium" size={20} strokeWidth={1.5} />
                  <h2 className="text-lg text-blueDarker font-semibold">Imobilizado</h2>
                </div>
                <CurrencyText size="small" value={data.totalFixedProperties} color="text-tealMedium" />
              </div>
            </div>
          </div>

          {/* Investimentos + detalhes */}
          <div className="bg-white rounded-custom20 px-4 md:px-6 lg:col-span-4 grid grid-cols-1 md:grid-cols-2 gap-4 items-center">
            <div className="w-full">
              <DonutChart2 data={data.investmentsChartData} height={220} />
            </div>
            <div className="flex flex-col gap-2">
              <h2 className="text-lg text-blueDarker font-semibold">Investimentos (BTG)</h2>
              <CurrencyText size="large" value={data.btgTotalWithoutPension} color="text-tealMedium" />
              <h2 className="text-lg text-blueDarker font-semibold">Previdência (BTG)</h2>
              <CurrencyText size="large" value={data.btgPensionTotal} color="text-tealMedium" />
            </div>
          </div>

          {/* Patrimônio livre de inventário */}
          <div className="bg-white rounded-custom20 py-4 px-6 lg:col-span-3 grid grid-cols-1 md:grid-cols-2 items-center gap-4 md:gap-2">
            <div className="flex justify-center">
              <ProgressDonutChart percentage={parseFloat(data.pensionPercent)} label="Previdência" />
            </div>
            <div className="flex justify-center">
              <ProgressDonutChart percentage={parseFloat(data.insurancePercent)} label="Seguro de vida" />
            </div>
            <div className="md:col-span-2 flex flex-col items-center mt-2">
              <h2 className="text-lg text-blueDarker font-semibold text-center">Patrimônio livre de inventário</h2>
              <CurrencyText size="large" value={data.inventoryFree} color="text-blueHighlight" />
            </div>
          </div>
        </div>

        {/* Linha 2 */}
        <div className="grid grid-cols-1 lg:grid-cols-10 gap-5">
          <div className="bg-white rounded-custom20 p-4 md:p-6 lg:col-span-7 flex flex-col gap-4 h-auto md:h-[460px]">
            <h2 className="text-lg text-blueDarker font-semibold">Independência financeira</h2>
            {data.retirementChartData.length > 0 ? (
              <div className="min-h-[240px] md:min-h-0 overflow-x-auto md:overflow-visible -mx-4 px-4 md:mx-0 md:px-0 touch-pan-x">
                {/* conteúdo com largura mínima para “forçar” a rolagem no mobile */}
                <div className="md:min-w-0" style={{ minWidth: '900px' }}>
                  <RetirementProjectionChart scenarios={data.retirementChartData} />
                </div>
              </div>
            ) : (
              <span className="text-sm text-blueDark">
                Nenhum cenário de independência financeira cadastrado como prioridade.
              </span>
            )}
          </div>

          <div className="flex flex-col lg:col-span-3 gap-5 h-auto md:h-[460px]">
            <div className="bg-white rounded-custom20 px-4 md:px-6 py-4 flex flex-col gap-2 h-full">
              <h2 className="text-lg text-blueDarker font-semibold">Insights</h2>
              <div className="flex flex-col gap-3 overflow-y-auto">
                <div className="flex flex-col gap-3 overflow-y-auto">
                  {data.retirementChartData.length > 0 ? (
                    insightTypes.some((t) => data.retirementChartData[0].insights?.[t]?.length > 0) ? (
                      insightTypes.map((type) => {
                        const items = data.retirementChartData[0].insights?.[type] || [];
                        if (items.length === 0) return null;
                        return (
                          <div key={type} className="flex flex-col gap-1">
                            {items.map((text, idx) => (
                              <DreamInsight
                                key={`${data.retirementChartData[0].scenarioId}-${type}-${idx}`}
                                type={type}
                                text={text}
                              />
                            ))}
                          </div>
                        );
                      })
                    ) : null
                  ) : (
                    <span className="text-sm text-blueDark">
                      Nenhum cenário de independência financeira cadastrado como prioridade.
                    </span>
                  )}
                </div>
              </div>
            </div>
          </div>

          {/* Evolução do Patrimônio (largura total) */}
          <div className="grid grid-cols-1 col-span-1 lg:grid-cols-10 gap-5 lg:col-span-10">
            <div className="bg-white rounded-custom20 p-4 md:p-6 lg:col-span-10 flex flex-col h-auto md:h-[460px]">
              <div className="flex justify-between items-center">
                <h2 className="text-lg text-blueDarker font-semibold">Evolução do Patrimônio</h2>
              </div>
              <div className="min-h-[240px] md:min-h-0 overflow-x-auto md:overflow-visible -mx-4 px-4 md:mx-0 md:px-0 touch-pan-x">
                <div className="md:min-w-0" style={{ minWidth: '1260px' }}>
                  <BarRoundedChartStack
                    data={data.patrimonyChartData}
                    labelKey="label"
                    series={[
                      { key: 'totalFixedProperties', label: 'Imobilizado', color: '#2d6386ff' },
                      { key: 'totalFinancialProperties', label: 'Financeiro (outros)', color: '#2D86B4' },
                      // { key: 'totalPension', label: 'Previdência', color: '#7AC3E0' },
                      // { key: 'totalInsurances', label: 'Seguros', color: '#9FD4E9' },
                      { key: 'totalBtg', label: 'Financeiro (BTG)', color: '#5BB2D7' },
                    ]}
                  />
                </div>
              </div>
            </div>
          </div>

          {/* Aportes */}
          <div className="grid grid-cols-1 lg:grid-cols-10 gap-5 lg:col-span-10">
            <div className="bg-white rounded-custom20 p-4 md:p-6 lg:col-span-7 flex flex-col gap-4 h-auto md:h-[460px]">
              <h2 className="text-lg text-blueDarker font-semibold">Aportes mensais / {data.currentYear}</h2>
              <div className="overflow-x-auto md:overflow-visible h-full">
                <BarRoundedChart data={data.monthlyContributions} className="h-full" />
              </div>
            </div>

            <div className="flex flex-col gap-5 h-auto md:h-[460px] lg:col-span-3 justify-between">
              <div className="bg-white rounded-custom20 w-full py-6 flex flex-col gap-2 items-center justify-center h-full">
                <HandCoins className="text-tealMedium" size={42} strokeWidth={1.25} />
                <h2 className="text-lg text-blueDarker font-semibold text-center">Aportes de {data.currentYear}</h2>
                <CurrencyText size="large" value={data.currentYearContributions} />
              </div>
              <div className="bg-white rounded-custom20 w-full py-6 flex flex-col gap-2 items-center justify-center h-full">
                <Banknote className="text-tealMedium" size={42} strokeWidth={1.25} />
                <h2 className="text-lg text-blueDarker font-semibold text-center">Aportes totais</h2>
                <CurrencyText size="large" value={data.totalContributions} />
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Dashboard;
